import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

/*
 * TimeseriesAnalyticsSection 역할:
 * - 선택된 시계열 데이터의 요약표, KPI, 추세 차트를 한 영역에 구성한다.
 * - 부모에서 계산한 통계값과 차트 데이터만 받아 분석 결과를 시각화한다.
 */
export default function TimeseriesAnalyticsSection({
  file,
  periodStart,
  periodEnd,
  getDisplayText,
  hasSummaryOpinion,
  comprehensiveOpinion,
  ultrasonicOpinion,
  temperatureOpinion,
  sortedSelectedFiles,
  averageUltrasonic,
  averageTemperature,
  averageThreePhaseDiffText,
  threePhaseCoverageTone,
  threePhaseCoveragePercent,
  threePhaseDiffValues,
  statusDistribution,
  statusDistributionTotal,
  statusDistributionPercentages,
  chartOptions,
  ultrasonicChartData,
  temperatureChartData,
  temperatureChartMode,
  onTemperatureChartModeChange,
  threePhaseDiffChartData,
  ultrasonicChartRef,
  temperatureChartRef,
}) {
  const { t } = useTranslation();
  const opinionLines = String(comprehensiveOpinion ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const selectedCountText = t("imageFileIndex.timeseries.kpi.countWithUnit", {
    count: sortedSelectedFiles.length,
    defaultValue: "{{count}}건",
  });
  const statusCountText = t("imageFileIndex.timeseries.kpi.countWithUnit", {
    count: statusDistributionTotal ?? 0,
    defaultValue: "{{count}}건",
  });
  const normalCountText = t("imageFileIndex.timeseries.kpi.countWithUnit", {
    count: statusDistribution?.normal ?? 0,
    defaultValue: "{{count}}건",
  });
  const warningCountText = t("imageFileIndex.timeseries.kpi.countWithUnit", {
    count: statusDistribution?.warning ?? 0,
    defaultValue: "{{count}}건",
  });
  const abnormalCountText = t("imageFileIndex.timeseries.kpi.countWithUnit", {
    count: statusDistribution?.abnormal ?? 0,
    defaultValue: "{{count}}건",
  });
  const normalLegendText = t(
    "imageFileIndex.timeseries.kpi.statusLegendItem",
    {
      label: t("imageFileIndex.timeseries.kpi.normal", {
        defaultValue: "정상",
      }),
      count: normalCountText,
      percent: statusDistributionPercentages?.normal ?? 0,
      defaultValue: "{{label}} {{count}} ({{percent}}%)",
    },
  );
  const warningLegendText = t(
    "imageFileIndex.timeseries.kpi.statusLegendItem",
    {
      label: t("imageFileIndex.timeseries.kpi.caution", {
        defaultValue: "요주의",
      }),
      count: warningCountText,
      percent: statusDistributionPercentages?.warning ?? 0,
      defaultValue: "{{label}} {{count}} ({{percent}}%)",
    },
  );
  const abnormalLegendText = t(
    "imageFileIndex.timeseries.kpi.statusLegendItem",
    {
      label: t("imageFileIndex.timeseries.kpi.abnormal", {
        defaultValue: "이상",
      }),
      count: abnormalCountText,
      percent: statusDistributionPercentages?.abnormal ?? 0,
      defaultValue: "{{label}} {{count}} ({{percent}}%)",
    },
  );

  return (
    <>
      <table className="equipmentTimeseriesReport summaryTable">
        <tbody>
          <tr>
            <th>
              {t("imageFileIndex.timeseries.summary.facilityType", {
                defaultValue: "설비 구분",
              })}
            </th>
            <th>
              {t("imageFileIndex.timeseries.summary.facilityName", {
                defaultValue: "설비명",
              })}
            </th>
            <th>
              {t("imageFileIndex.timeseries.summary.partName", {
                defaultValue: "부품명",
              })}
            </th>
            <th>
              {t("imageFileIndex.timeseries.summary.analysisPeriod", {
                defaultValue: "분석 기간",
              })}
            </th>
          </tr>
          <tr>
            <td>{getDisplayText(file?.facility_type)}</td>
            <td>{getDisplayText(file?.facility_name)}</td>
            <td>{getDisplayText(file?.part_name)}</td>
            <td>
              {getDisplayText(periodStart?.shooting_date)} ~{" "}
              {getDisplayText(periodEnd?.shooting_date)}
            </td>
          </tr>
          {hasSummaryOpinion ? (
            <>
              <tr>
                <th colSpan={4}>
                  {t("imageFileIndex.timeseries.summary.comprehensiveOpinion", {
                  defaultValue: "종합의견",
                })}
                  </th>
              </tr>
              <tr>
                <td
                  colSpan={4}
                  className="equipmentTimeseriesReport summaryOpinionCell"
                >
                  {opinionLines.length > 0 ? (
                    opinionLines.map((line, index) => (
                      <p
                        className="equipmentTimeseriesReport summaryOpinionLine"
                        key={`timeseries-summary-opinion-${index}`}
                      >
                        {line}
                      </p>
                    ))
                  ) : (
                    <>
                      <p className="equipmentTimeseriesReport summaryOpinionLine">
                        {ultrasonicOpinion}
                      </p>
                      <p className="equipmentTimeseriesReport summaryOpinionLine">
                        {temperatureOpinion}
                      </p>
                    </>
                  )}
                </td>
              </tr>
            </>
          ) : null}
        </tbody>
      </table>

      <div className="equipmentTimeseriesReport kpiGrid">
        <div className="equipmentTimeseriesReport kpiCard isDataCount">
          <span className="equipmentTimeseriesReport kpiLabel">
            {t("imageFileIndex.timeseries.kpi.dataCount", {
              defaultValue: "분석 데이터 수",
            })}
          </span>
          <strong className="equipmentTimeseriesReport kpiValue">
            {selectedCountText}
          </strong>
          <p className="equipmentTimeseriesReport kpiSubtext">
            {`${getDisplayText(periodStart?.shooting_date)} ~ ${getDisplayText(periodEnd?.shooting_date)}`}
          </p>
        </div>

        <div className="equipmentTimeseriesReport kpiCard isUltrasonic">
          <span className="equipmentTimeseriesReport kpiLabel">
            {t("imageFileIndex.timeseries.kpi.averageUltrasonic", {
              defaultValue: "평균 초음파",
            })}
          </span>
          <strong className="equipmentTimeseriesReport kpiValue">
            {averageUltrasonic === "-" ? "-" : `${averageUltrasonic} dB`}
          </strong>
          <p className="equipmentTimeseriesReport kpiSubtext">
            {t("imageFileIndex.timeseries.kpi.averageBySelection", {
              defaultValue: "전체 선택 데이터 기준 평균값",
            })}
          </p>
        </div>

        <div className="equipmentTimeseriesReport kpiCard isTemperature">
          <span className="equipmentTimeseriesReport kpiLabel">
            {t("imageFileIndex.timeseries.kpi.averageTemperatureAndThreePhase", {
              defaultValue: "평균 온도 / 평균 3상 비교 온도차",
            })}
          </span>
          <strong className="equipmentTimeseriesReport kpiValue">
            {averageTemperature === "-" ? "-" : `${averageTemperature} °C`}
          </strong>
          <p className="equipmentTimeseriesReport kpiSubtext">
            {t("imageFileIndex.timeseries.kpi.averageThreePhaseDifference", {
              value: averageThreePhaseDiffText,
              defaultValue: "평균 3상 비교 온도차 {{value}}",
            })}
          </p>
        </div>

        <div className="equipmentTimeseriesReport kpiCard isStatusRatio">
          <div className="equipmentTimeseriesReport kpiGaugeWrap">
            <div
              className="equipmentTimeseriesReport kpiGauge isStatusMix"
              style={{
                "--status-normal": `${statusDistributionPercentages?.normal ?? 0}%`,
                "--status-warning": `${statusDistributionPercentages?.warning ?? 0}%`,
                "--status-abnormal": `${statusDistributionPercentages?.abnormal ?? 0}%`,
              }}
            >
              <div className="equipmentTimeseriesReport kpiGaugeInner">
                <strong>{statusDistributionTotal ?? 0}</strong>
                <span>
                  {t("imageFileIndex.timeseries.kpi.statusDistribution", {
                    defaultValue: "판정",
                  })}
                </span>
              </div>
            </div>
          </div>
          <div className="equipmentTimeseriesReport kpiText">
            <span className="equipmentTimeseriesReport kpiLabel">
              {t("imageFileIndex.timeseries.kpi.statusRatio", {
                defaultValue: "정상, 요주의, 이상 비율",
              })}
            </span>
            <strong className="equipmentTimeseriesReport kpiValue">
              {statusCountText}
            </strong>
            <div className="equipmentTimeseriesReport statusLegend">
              <span className="equipmentTimeseriesReport statusLegendItem">
                <span className="equipmentTimeseriesReport statusDot isNormal" />
                {normalLegendText}
              </span>
              <span className="equipmentTimeseriesReport statusLegendItem">
                <span className="equipmentTimeseriesReport statusDot isWarning" />
                {warningLegendText}
              </span>
              <span className="equipmentTimeseriesReport statusLegendItem">
                <span className="equipmentTimeseriesReport statusDot isAbnormal" />
                {abnormalLegendText}
              </span>
            </div>
          </div>
        </div>

        <div className="equipmentTimeseriesReport kpiCard isHighlight">
          <div className="equipmentTimeseriesReport kpiGaugeWrap">
            <div
              className={`equipmentTimeseriesReport kpiGauge ${threePhaseCoverageTone}`}
              style={{ "--kpi-progress": `${threePhaseCoveragePercent}%` }}
            >
              <div className="equipmentTimeseriesReport kpiGaugeInner">
                <strong>{threePhaseCoveragePercent}%</strong>
                <span>
                  {t("imageFileIndex.timeseries.kpi.validRate", {
                    defaultValue: "분석률",
                  })}
                </span>
              </div>
            </div>
          </div>
          <div className="equipmentTimeseriesReport kpiText">
            <span className="equipmentTimeseriesReport kpiLabel">
              {t("imageFileIndex.timeseries.kpi.coverageRate", {
                defaultValue: "3상 비교 분석 완료율",
              })}
            </span>
            <strong className="equipmentTimeseriesReport kpiValue">
              {`${threePhaseDiffValues.length} / ${sortedSelectedFiles.length}`}
            </strong>
            <p className="equipmentTimeseriesReport kpiSubtext">
              {t("imageFileIndex.timeseries.kpi.coverageHelp", {
                defaultValue:
                  "3상 비교 분석이 수행된 데이터만 온도차 분석에 반영됩니다.",
              })}
            </p>
          </div>
        </div>
      </div>

      <div className="equipmentTimeseriesReport chartGrid">
        <div className="equipmentTimeseriesReport chartCard ultrasonicChartCard">
          <div className="equipmentTimeseriesReport chartHeader">
            <div className="equipmentTimeseriesReport chartHeaderMain">
              <strong>
                {t("imageFileIndex.timeseries.chart.ultrasonicTrend", {
                  defaultValue: "초음파 추이 그래프",
                })}
              </strong>
              <div
                className="equipmentTimeseriesReport chartSwitchGroup isPlaceholder"
                aria-hidden="true"
              >
                <span className="equipmentTimeseriesReport chartSwitchButton">
                  {t("imageFileIndex.timeseries.chart.temperaturePlaceholder", {
                    defaultValue: "온도",
                  })}
                </span>
                <span className="equipmentTimeseriesReport chartSwitchButton">
                  {t(
                    "imageFileIndex.timeseries.chart.threePhaseDifferencePlaceholder",
                    {
                      defaultValue: "3상 비교 온도차",
                    },
                  )}
                </span>
              </div>
            </div>
            <span>
              {getDisplayText(periodStart?.shooting_date)} ~{" "}
              {getDisplayText(periodEnd?.shooting_date)}
            </span>
          </div>
          <div className="equipmentTimeseriesReport chartCanvas">
            <Line
              ref={ultrasonicChartRef}
              data={ultrasonicChartData}
              options={chartOptions}
            />
          </div>
        </div>

        <div className="equipmentTimeseriesReport chartCard">
          <div className="equipmentTimeseriesReport chartHeader">
            <div className="equipmentTimeseriesReport chartHeaderMain">
              <strong>
                {t("imageFileIndex.timeseries.chart.temperatureTrend", {
                  defaultValue: "온도 추이 그래프",
                })}
              </strong>
              <div className="equipmentTimeseriesReport chartSwitchGroup">
                <button
                  type="button"
                  className={`equipmentTimeseriesReport chartSwitchButton${
                    temperatureChartMode === "thermalCenter" ? " isActive" : ""
                  }`}
                  onClick={() => onTemperatureChartModeChange("thermalCenter")}
                >
                  {t("imageFileIndex.timeseries.chart.detectedTemperature", {
                    defaultValue: "검출 온도",
                  })}
                </button>
                <button
                  type="button"
                  className={`equipmentTimeseriesReport chartSwitchButton${
                    temperatureChartMode === "threePhaseDiff"
                      ? " isActive"
                      : ""
                  }`}
                  onClick={() => onTemperatureChartModeChange("threePhaseDiff")}
                >
                  {t("imageFileIndex.timeseries.chart.threePhaseDifference", {
                    defaultValue: "3상 비교 온도차",
                  })}
                </button>
              </div>
            </div>
            <span>
              {getDisplayText(periodStart?.shooting_date)} ~{" "}
              {getDisplayText(periodEnd?.shooting_date)}
            </span>
          </div>
          <div className="equipmentTimeseriesReport chartCanvas">
            <Line
              ref={temperatureChartRef}
              data={temperatureChartData}
              options={chartOptions}
            />
          </div>
          {temperatureChartMode === "threePhaseDiff" &&
          threePhaseDiffChartData.labels.length === 0 ? (
            <p className="equipmentTimeseriesReport chartEmptyText">
              {t("imageFileIndex.timeseries.chart.emptyThreePhaseDifference", {
                defaultValue:
                  "3상 비교 온도차 값이 있는 데이터만 그래프에 표시됩니다.",
              })}
            </p>
          ) : null}
        </div>
      </div>
    </>
  );
}
